import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token management
export const tokenManager = {
  getToken: () => localStorage.getItem('hiking_auth_token'),
  setToken: (token) => localStorage.setItem('hiking_auth_token', token),
  removeToken: () => localStorage.removeItem('hiking_auth_token'),
  isTokenExpired: () => {
    const token = tokenManager.getToken();
    if (!token) return true;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000 < Date.now();
    } catch {
      return true;
    }
  }
};

// Add token to requests
api.interceptors.request.use((config) => {
  const token = tokenManager.getToken();
  if (token && !tokenManager.isTokenExpired()) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error);
    
    if (error.response) {
      const errorData = error.response.data;
      
      // Token expired or unauthorized
      if (error.response.status === 401 || error.response.status === 403) {
        tokenManager.removeToken();
        window.location.href = '/login';
        throw new Error('Session expired. Please login again.');
      }
      
      // Too many requests (brute force protection)
      if (error.response.status === 429) {
        const message = errorData.error || 'Too many attempts. Please try again later.';
        if (errorData.remainingAttempts !== undefined && errorData.remainingAttempts > 0) {
          throw new Error(`${message} (${errorData.remainingAttempts} attempts remaining)`);
        }
        throw new Error(message);
      }
      
      // Invalid password with remaining attempts
      if (error.response.status === 401 && errorData.remainingAttempts !== undefined) {
        throw new Error(`${errorData.error} (${errorData.remainingAttempts} attempts remaining)`);
      }
      
      // Server responded with error status
      throw new Error(errorData.error || 'Server error occurred');
    } else if (error.request) {
      // Network error
      throw new Error('Unable to connect to server. Please check your connection.');
    } else {
      // Other error
      throw new Error('An unexpected error occurred');
    }
  }
);

export const authAPI = {
  // Login with password
  login: (password) => api.post('/auth/login', { password }),
  
  // Verify current token
  verify: () => api.get('/auth/verify'),
  
  // Get login status (failed attempts, etc.)
  getStatus: () => api.get('/auth/status'),
  
  // Logout
  logout: () => api.post('/auth/logout'),
};

export const journeyAPI = {
  // Get all active journeys
  getJourneys: () => api.get('/journeys'),
  
  // Get specific journey by ID
  getJourney: (id) => api.get(`/journeys/${id}`),
  
  // Create new journey alert
  createJourney: (journeyData) => api.post('/journeys', journeyData),
  
  // Complete journey (mark as safe return)
  completeJourney: (id) => api.put(`/journeys/${id}/complete`),
  
  // Cancel journey alert
  cancelJourney: (id) => api.delete(`/journeys/${id}`),
  
  // Get notification status overview
  getStatus: () => api.get('/journeys/status/overview'),
  
  // Trigger manual notification check (for testing)
  checkNotifications: () => api.post('/journeys/notifications/check'),
  
  // Test Notification_API connection
  testNotificationApi: () => api.post('/journeys/notifications/test'),
  
  // Health check
  healthCheck: () => api.get('/health'),
};

export default api;
